/**
 * Generic API client utilities for making HTTP requests.
 * Provides consistent error handling, type safety, and request cancellation support.
 */

/**
 * Custom error class for API-related errors.
 * Extends the native Error class with additional context about the failed request.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly endpoint: string,
    public readonly statusCode?: number,
    public readonly responseBody?: string,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = "ApiError";

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * Options for configuring API requests.
 */
export interface ApiRequestOptions extends RequestInit {
  signal?: AbortSignal;
  queryParams?: Record<string, string | number | boolean>;
  timeout?: number;
}

/**
 * Configuration for the API client.
 */
export interface ApiClientConfig {
  baseUrl: string;
  defaultHeaders?: HeadersInit;
  /** Headers evaluated on every request (for values that can change over the client's lifetime). */
  dynamicHeaders?: () => Record<string, string>;
  defaultTimeout?: number;
}

/**
 * Generic API client for making HTTP requests.
 * Supports GET and POST methods for the Provet booking API.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly defaultHeaders: HeadersInit;
  private readonly defaultTimeout: number;
  private readonly dynamicHeaders?: () => Record<string, string>;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.dynamicHeaders = config.dynamicHeaders;
    this.defaultHeaders = config.defaultHeaders || {
      "Content-Type": "application/json"
    };
    this.defaultTimeout = config.defaultTimeout || 30000;
  }

  private buildUrl(
    endpoint: string,
    queryParams?: Record<string, string | number | boolean>
  ): string {
    const url = `${this.baseUrl}${endpoint}`;

    if (!queryParams || Object.keys(queryParams).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });

    return `${url}?${searchParams.toString()}`;
  }

  private createTimeoutPromise(timeoutMs: number, endpoint: string): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(
          new ApiError(
            `Request timeout after ${timeoutMs}ms`,
            endpoint,
            undefined,
            undefined,
            new Error("TIMEOUT")
          )
        );
      }, timeoutMs);
    });
  }

  private async request(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<Response> {
    const { queryParams, timeout = this.defaultTimeout, ...fetchOptions } = options;

    const url = this.buildUrl(endpoint, queryParams);

    const headers = new Headers(this.defaultHeaders);
    if (this.dynamicHeaders) {
      Object.entries(this.dynamicHeaders()).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }
    if (fetchOptions.headers) {
      const optionHeaders = new Headers(fetchOptions.headers);
      optionHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }

    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers
    };

    try {
      const response = await Promise.race([
        fetch(url, requestOptions),
        this.createTimeoutPromise(timeout, endpoint)
      ]);

      if (!response.ok) {
        const responseBody = await response
          .text()
          .catch(() => "Unable to read response body");

        throw new ApiError(
          `HTTP Error: ${response.status} ${response.statusText}`,
          endpoint,
          response.status,
          responseBody
        );
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      throw new ApiError(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
        endpoint,
        undefined,
        undefined,
        error
      );
    }
  }

  /**
   * Performs a GET request.
   * @param endpoint - The API endpoint (relative to baseUrl)
   * @param options - Optional request configuration
   * @returns Promise resolving to the parsed JSON response
   */
  async get<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    const response = await this.request(endpoint, {
      ...options,
      method: "GET"
    });

    try {
      return await response.json();
    } catch (error) {
      throw new ApiError(
        "Failed to parse JSON response",
        endpoint,
        response.status,
        await response.text().catch(() => "Unable to read response body"),
        error
      );
    }
  }

  /**
   * Performs a POST request.
   * @param endpoint - The API endpoint (relative to baseUrl)
   * @param body - The request body (will be JSON stringified if object)
   * @param options - Optional request configuration
   * @returns Promise resolving to the parsed JSON response
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: ApiRequestOptions
  ): Promise<T> {
    const response = await this.request(endpoint, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined
    });

    try {
      return await response.json();
    } catch (error) {
      throw new ApiError(
        "Failed to parse JSON response",
        endpoint,
        response.status,
        await response.text().catch(() => "Unable to read response body"),
        error
      );
    }
  }
}

/**
 * Whether the request was given up because it took longer than its timeout.
 */
export function isTimeoutError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.originalError instanceof Error &&
    error.originalError.message === "TIMEOUT"
  );
}

/**
 * Whether a booking submission ended without knowing if the booking was created: the browser
 * gave up waiting, or the clinic system didn't confirm it in time (504). The booking may exist,
 * so it must not be sent again.
 */
export function isUnconfirmedBookingError(error: unknown): boolean {
  return isTimeoutError(error) || (error instanceof ApiError && error.statusCode === 504);
}

/**
 * Whether the request failed without a response from the server: a network error or a timeout.
 */
export function isConnectionError(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === undefined;
}

/**
 * Helper function to check if an error is an AbortError.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/**
 * Helper function to safely log API errors with context.
 */
export function logApiError(error: unknown, context?: string): void {
  if (error instanceof ApiError) {
    const logPrefix = context ? `[${context}]` : "[API Error]";
    console.error(`${logPrefix} ${error.message}`, {
      endpoint: error.endpoint,
      statusCode: error.statusCode,
      responseBody: error.responseBody
    });
  } else if (isAbortError(error)) {
    return;
  } else {
    console.error(context ? `[${context}]` : "[Unexpected Error]", error);
  }
}
